import { useState, useEffect, useRef } from "react";
import {
  Save,
  Edit,
  User,
  Key,
  AlertCircle,
  Shield,
  MapPin,
  Calendar,
  Clock,
  CheckSquare,
  XSquare,
  ArrowRight,
  Mail,
  Briefcase,
  Quote,
  Upload,
} from "lucide-react";
import { API_ENDPOINTS } from "@/constants/constants";

// Interface pour les informations du profil
interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  fonction: string;
  service: string;
  birthdate: string;
  sponsorId?: string | null;
  profileImage?: string | null;
  citation?: string | null;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "profile" | "security" | "activity"
  >("profile");

  // États pour les formulaires d'édition
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // États pour les données du formulaire
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    fonction: "",
    service: "",
    birthdate: "",
    email: "",
    sponsorId: "",
    citation: "",
    image: "",
    tempImagePreview: undefined as string | undefined,
  });

  // États pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Simulation des activités récentes
  const [recentActivities] = useState([
    {
      id: 1,
      action: "Connexion réussie",
      date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: "success",
    },
    {
      id: 2,
      action: "Modification du profil",
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      status: "success",
    },
    {
      id: 3,
      action: "Tentative de connexion",
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      status: "error",
    },
    {
      id: 4,
      action: "Changement de mot de passe",
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: "success",
    },
  ]);

  // Référence pour l'input de fichier
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Récupérer les informations du profil au chargement
  useEffect(() => {
    fetchProfile();
  }, []);

  // Mettre à jour le formData lorsque le profil est chargé
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName,
        lastName: profile.lastName,
        fonction: profile.fonction,
        service: profile.service,
        birthdate: profile.birthdate,
        email: profile.email,
        sponsorId: profile.sponsorId || "",
        citation: profile.citation || "",
        image: profile.profileImage || "",
        tempImagePreview: undefined,
      });
    }
  }, [profile]);

  // Fonction pour récupérer les informations du profil
  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const userStr = localStorage.getItem("user");
      const accessToken = localStorage.getItem("accessToken");

      if (!userStr || !accessToken) {
        throw new Error("Session non valide. Veuillez vous reconnecter.");
      }

      const userData = JSON.parse(userStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error("Identifiant utilisateur non trouvé");
      }

      const response = await fetch(API_ENDPOINTS.AUTH.UPDATE_USER(userId), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            "Impossible de récupérer les informations du profil"
        );
      }

      const profileData = await response.json();

      setProfile({
        id: profileData.id,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        role: profileData.role,
        fonction: profileData.fonction,
        service: profileData.service,
        birthdate: profileData.birthdate,
        sponsorId: profileData.sponsor_id,
        profileImage: profileData.profileImage,
        citation: profileData.citation,
        isActive: profileData.isActive,
        isVerified: profileData.isVerified,
        createdAt: profileData.createdAt,
        updatedAt: profileData.updatedAt,
      });

      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour le profil
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      if (!profile) {
        throw new Error("Profil non trouvé");
      }

      // Si un fichier est sélectionné, l'uploader d'abord
      if (selectedFile) {
        const imagePath = await handleFileUpload(selectedFile);
        if (imagePath) {
          formData.image = imagePath;
        }
      }

      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fonction: formData.fonction,
        service: formData.service,
        birthdate: formData.birthdate,
        email: formData.email,
        sponsorId: formData.sponsorId || null,
        citation: formData.citation || null,
        image: formData.image || null,
        role: profile.role,
      };

      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Session non valide. Veuillez vous reconnecter.");
      }

      // Appeler l'endpoint API
      const response = await fetch(API_ENDPOINTS.AUTH.UPDATE_USER(profile.id), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la mise à jour du profil"
        );
      }

      const updatedUserData = await response.json();

      // Mettre à jour le profil local
      const updatedProfile = {
        ...profile,
        ...updatedUserData,
      };

      setProfile(updatedProfile);

      // Mettre à jour les données utilisateur dans le localStorage
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userData = JSON.parse(userStr);
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...userData,
            ...updatedUserData,
          })
        );
      }

      setSuccess("Profil mis à jour avec succès");
      setIsEditingProfile(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors de la mise à jour du profil"
      );
      setLoading(false);
    }
  };

  // Fonction pour changer le mot de passe
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Vérifier que les mots de passe correspondent
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!profile) {
        throw new Error("Profil non trouvé");
      }

      const passwordUpdateData = {
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword,
      };

      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Session non valide. Veuillez vous reconnecter.");
      }

      // Appeler l'endpoint API pour mettre à jour le mot de passe
      const response = await fetch(API_ENDPOINTS.AUTH.UPDATE_USER(profile.id), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(passwordUpdateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors du changement de mot de passe"
        );
      }

      setSuccess("Mot de passe modifié avec succès");
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors du changement de mot de passe"
      );
      setLoading(false);
    }
  };

  // Gérer les changements dans le formulaire du profil
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Gérer les changements dans le formulaire de mot de passe
  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  // Réinitialiser les messages d'erreur/succès quand on change de mode d'édition
  useEffect(() => {
    setError(null);
    setSuccess(null);
  }, [isEditingProfile, isChangingPassword]);

  // Formater une date pour l'affichage
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Non disponible";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Fonction pour gérer l'upload de fichier
  const handleFileUpload = async (file: File): Promise<string | null> => {
    try {
      setUploadProgress(0);
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const formData = new FormData();
      formData.append("file", file);

      const uploadUrl = API_ENDPOINTS.AUTH.UPLOAD_FILE;

      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise<string>((resolve, reject) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response.path || response.url || response.image || "");
            } catch {
              reject(new Error("Réponse invalide du serveur"));
            }
          } else {
            reject(new Error(`Erreur lors de l'upload: ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Échec de la connexion réseau"));
        });

        xhr.addEventListener("abort", () => {
          reject(new Error("Upload annulé"));
        });

        xhr.open("POST", uploadUrl, true);
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
        xhr.send(formData);
      });

      const imagePath = await uploadPromise;
      setUploadProgress(100);

      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);

      return imagePath;
    } catch (error) {
      console.error("Erreur d'upload:", error);
      setError(
        error instanceof Error ? error.message : "Erreur lors de l'upload"
      );
      setUploadProgress(0);
      return null;
    }
  };

  // Gérer les changements dans l'input de fichier
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Nettoyer l'aperçu précédent si existant
      if (formData.tempImagePreview) {
        URL.revokeObjectURL(formData.tempImagePreview);
      }

      // Créer une URL pour l'aperçu de l'image
      const imageUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, tempImagePreview: imageUrl }));
    }
  };

  // Afficher un message de chargement pendant la récupération des données
  if (loading && !profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header avec photo et informations de base */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 mb-6 text-white">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="relative">
              {profile?.profileImage ? (
                <div className="h-28 w-28 rounded-full bg-white flex items-center justify-center shadow-lg border-4 border-white overflow-hidden">
                  <img
                    src={profile.profileImage}
                    alt={`${profile.firstName} ${profile.lastName}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-28 w-28 rounded-full bg-white flex items-center justify-center shadow-lg border-4 border-white">
                  <User size={64} className="text-blue-700" />
                </div>
              )}
              {!isEditingProfile && (
                <button
                  onClick={() => setIsEditingProfile(true)}
                  className="absolute bottom-0 right-0 bg-white text-blue-700 rounded-full p-2 shadow-md hover:bg-blue-50 transition-colors"
                >
                  <Edit size={16} />
                </button>
              )}
            </div>

            <div className="text-center sm:text-left">
              <h1 className="text-2xl font-bold">
                {profile?.firstName} {profile?.lastName}
              </h1>
              <div className="flex items-center justify-center sm:justify-start mt-2 text-blue-100">
                <Shield size={16} className="mr-1" />
                <span className="capitalize">{profile?.role}</span>
              </div>
              <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                <Mail size={16} className="mr-1" />
                <span>{profile?.email}</span>
              </div>
              {profile?.fonction && (
                <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                  <Briefcase size={16} className="mr-1" />
                  <span>{profile.fonction}</span>
                </div>
              )}
              {profile?.service && (
                <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                  <MapPin size={16} className="mr-1" />
                  <span>{profile.service}</span>
                </div>
              )}
              <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                <Calendar size={16} className="mr-1" />
                <span>
                  Membre depuis le{" "}
                  {profile ? formatDate(profile.createdAt).split("à")[0] : ""}
                </span>
              </div>
              {profile?.birthdate && (
                <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                  <Calendar size={16} className="mr-1" />
                  <span>
                    Né(e) le{" "}
                    {new Date(profile.birthdate).toLocaleDateString("fr-FR")}
                  </span>
                </div>
              )}
              {profile?.citation && (
                <div className="flex items-center justify-center sm:justify-start mt-1 text-blue-100">
                  <Quote size={16} className="mr-1" />
                  <span>"{profile.citation}"</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages de succès ou d'erreur */}
        {success && (
          <div
            className="bg-green-100 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded mb-6 flex items-start"
            role="alert"
          >
            <CheckSquare className="h-5 w-5 mr-2 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-6 flex items-start"
            role="alert"
          >
            <XSquare className="h-5 w-5 mr-2 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Navigation par onglets */}
        <div className="bg-white rounded-lg shadow-md mb-6 p-1">
          <div className="border-b flex">
            <button
              onClick={() => setActiveTab("profile")}
              className={`flex items-center py-3 px-6 font-medium transition-colors ${
                activeTab === "profile"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-blue-600"
              }`}
            >
              <User size={18} className="mr-2" />
              Profil
            </button>
            <button
              onClick={() => setActiveTab("security")}
              className={`flex items-center py-3 px-6 font-medium transition-colors ${
                activeTab === "security"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-blue-600"
              }`}
            >
              <Key size={18} className="mr-2" />
              Sécurité
            </button>
            <button
              onClick={() => setActiveTab("activity")}
              className={`flex items-center py-3 px-6 font-medium transition-colors ${
                activeTab === "activity"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-blue-600"
              }`}
            >
              <Clock size={18} className="mr-2" />
              Activité
            </button>
          </div>
        </div>

        {/* Contenu des onglets */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Onglet Profil */}
          {activeTab === "profile" && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                Informations personnelles
              </h2>

              {/* Vue des informations */}
              {!isEditingProfile ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Prénom</p>
                      <p className="font-medium">{profile?.firstName}</p>
                    </div>

                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Nom</p>
                      <p className="font-medium">{profile?.lastName}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Fonction</p>
                      <p className="font-medium">
                        {profile?.fonction || "Non spécifiée"}
                      </p>
                    </div>

                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Service</p>
                      <p className="font-medium">
                        {profile?.service || "Non spécifié"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <p className="font-medium">{profile?.email}</p>
                    </div>

                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">
                        Date de naissance
                      </p>
                      <p className="font-medium">
                        {profile?.birthdate
                          ? new Date(profile.birthdate).toLocaleDateString(
                              "fr-FR"
                            )
                          : "Non spécifiée"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">Rôle</p>
                      <p className="font-medium capitalize">{profile?.role}</p>
                    </div>

                    <div className="border-b pb-4">
                      <p className="text-sm text-gray-500 mb-1">ID Parrain</p>
                      <p className="font-medium">
                        {profile?.sponsorId || "Non défini"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Citation</p>
                    <p className="font-medium">
                      {profile?.citation || "Aucune citation ajoutée"}
                    </p>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={() => setIsEditingProfile(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Modifier le profil
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Prénom
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Nom
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="fonction"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Fonction
                    </label>
                    <input
                      type="text"
                      id="fonction"
                      name="fonction"
                      value={formData.fonction}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Service
                    </label>
                    <input
                      type="text"
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="birthdate"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      id="birthdate"
                      name="birthdate"
                      value={formData.birthdate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="citation"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Citation
                    </label>
                    <input
                      type="text"
                      id="citation"
                      name="citation"
                      value={formData.citation}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="image"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Image de profil
                    </label>
                    <div className="flex items-center gap-4">
                      {/* Aperçu de l'image (thumbnail carré) */}
                      <div className="relative w-24 h-24 border border-gray-300 rounded-md overflow-hidden flex items-center justify-center bg-gray-100">
                        {formData.tempImagePreview || formData.image ? (
                          <img
                            src={formData.tempImagePreview || formData.image}
                            alt="Image de profil"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "";
                              (e.target as HTMLImageElement).alt =
                                "Image non disponible";
                            }}
                          />
                        ) : (
                          <User size={36} className="text-gray-400" />
                        )}
                      </div>

                      {/* Bouton upload */}
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-md"
                        >
                          <Upload size={16} />
                          <span>Upload</span>
                        </button>

                        {selectedFile && uploadProgress > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                        )}
                      </div>

                      {/* Champ caché pour l'URL de l'image */}
                      <input
                        type="hidden"
                        name="image"
                        value={formData.image || ""}
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {loading ? (
                        <span className="inline-flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Enregistrement...
                        </span>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Enregistrer
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingProfile(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Onglet Sécurité */}
          {activeTab === "security" && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Sécurité du compte</h2>

              {!isChangingPassword ? (
                <div>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Pour maintenir la sécurité de votre compte, nous vous
                          recommandons de changer votre mot de passe
                          régulièrement et d'utiliser un mot de passe unique et
                          fort.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 border rounded-lg mb-6">
                    <h3 className="font-medium flex items-center">
                      <Key className="mr-2 h-5 w-5 text-gray-400" />
                      Mot de passe
                    </h3>
                    <p className="text-gray-500 text-sm mt-2">
                      Dernière modification:{" "}
                      {profile?.updatedAt
                        ? formatDate(profile.updatedAt)
                        : "Non disponible"}
                    </p>
                    <button
                      onClick={() => setIsChangingPassword(true)}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Changer le mot de passe
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handlePasswordChange}
                  className="bg-white p-6 border rounded-lg space-y-4"
                >
                  <div>
                    <label
                      htmlFor="currentPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Mot de passe actuel
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Nouveau mot de passe
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirmer le nouveau mot de passe
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="pt-2 flex space-x-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {loading ? (
                        <span className="inline-flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Enregistrement...
                        </span>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Enregistrer
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsChangingPassword(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Onglet Activité */}
          {activeTab === "activity" && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Activité récente</h2>

              <div className="overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {recentActivities.map((activity) => (
                    <li key={activity.id} className="py-4">
                      <div className="flex items-start space-x-4">
                        <div
                          className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                            activity.status === "success"
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          {activity.status === "success" ? (
                            <CheckSquare className="h-5 w-5 text-green-600" />
                          ) : (
                            <XSquare className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.action}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDate(activity.date)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              activity.status === "success"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {activity.status === "success"
                              ? "Réussi"
                              : "Échoué"}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}

                  <li className="py-4">
                    <div className="flex justify-center">
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                        Voir plus d'activités
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
